import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTag,
  FaExclamationTriangle,
  FaCheck,
  FaUsers,
  FaInfoCircle
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { auth } from '../../firebaseConfig';
import styles from './CategoryManagementModal.module.css';

const CategoryManagementModal = ({ isOpen, onClose, clubId, onCategoriesUpdated }) => {
  const { userProfile } = useUser();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form states
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen && clubId) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clubId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token (optional for fetching, but good for consistency)
      const token = await auth.currentUser?.getIdToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${backendUrl}/api/categories/club/${clubId}`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ name: '', description: '' });
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (category) => {
    setEditingId(category._id);
    setIsAdding(false);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', description: '' });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setError('You must be logged in to manage categories');
        setLoading(false);
        return;
      }
      
      const url = isAdding 
        ? `${backendUrl}/api/categories`
        : `${backendUrl}/api/categories/${editingId}`;
      
      const method = isAdding ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          clubId,
          createdBy: userProfile?._id || clubId // Use user ID or fallback to club ID
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save category');
      }

      await response.json();
      setSuccess(isAdding ? 'Category added successfully!' : 'Category updated successfully!');
      
      // Refresh categories list
      await fetchCategories();
      
      // Reset form
      handleCancel();
      
      // Notify parent component
      if (onCategoriesUpdated) {
        onCategoriesUpdated();
      }
      
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Failed to save category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setError('You must be logged in to delete categories');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete category');
      }

      setSuccess('Category deleted successfully!');
      
      // Refresh categories list
      await fetchCategories();
      
      // Notify parent component
      if (onCategoriesUpdated) {
        onCategoriesUpdated();
      }
      
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message || 'Failed to delete category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaTag className={styles.modalTitleIcon} />
            Manage Categories
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Error/Success Messages */}
          {error && (
            <div className={styles.errorMessage}>
              <FaExclamationTriangle className={styles.errorIcon} />
              {error}
            </div>
          )}
          
          {success && (
            <div className={styles.successMessage}>
              <FaCheck className={styles.successIcon} />
              {success}
            </div>
          )}

          {/* Add/Edit Form */}
          {(isAdding || editingId) && (
            <div className={styles.formSection}>
              <h3 className={styles.formTitle}>
                {isAdding ? 'Add New Category' : 'Edit Category'}
              </h3>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.formLabel}>
                    Category Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={styles.formInput}
                    placeholder="Enter category name"
                    required
                    maxLength={50}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="description" className={styles.formLabel}>
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={styles.formTextarea}
                    placeholder="Enter category description (optional)"
                    rows={3}
                    maxLength={200}
                  />
                </div>
                
                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={styles.saveButton}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (isAdding ? 'Add Category' : 'Update Category')}
                  </button>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Categories List */}
          <div className={styles.categoriesSection}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Current Categories</h3>
              {!isAdding && !editingId && (
                <button
                  className={styles.addButton}
                  onClick={handleAddNew}
                  disabled={loading}
                >
                  <FaPlus /> Add Category
                </button>
              )}
            </div>

            {loading && !isAdding && !editingId ? (
              <div className={styles.loadingMessage}>
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className={styles.emptyState}>
                <FaTag className={styles.emptyIcon} />
                <p>No categories created yet.</p>
                <p>Create your first category to get started!</p>
              </div>
            ) : (
              <div className={styles.categoriesList}>
                {categories.map(category => (
                  <div key={category._id} className={styles.categoryItem}>
                    <div className={styles.categoryInfo}>
                      <div className={styles.categoryName}>
                        <FaTag className={styles.categoryIcon} />
                        {category.name}
                      </div>
                      {category.description && (
                        <div className={styles.categoryDescription}>
                          {category.description}
                        </div>
                      )}
                      <div className={styles.categoryMeta}>
                        <span className={styles.usageCount}>
                          <FaUsers className={styles.metaIcon} />
                          {category.usageCount || 0} programs
                        </span>
                        {category.isDefault && (
                          <span className={styles.defaultBadge}>
                            <FaInfoCircle className={styles.metaIcon} />
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className={styles.categoryActions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEdit(category)}
                        disabled={loading}
                        title="Edit category"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(category._id, category.name)}
                        disabled={loading || (category.usageCount > 0)}
                        title={category.usageCount > 0 ? "Cannot delete category in use" : "Delete category"}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal; 